import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Polygon, PointCoordinates } from 'models/map'
import { useChangeArea } from 'services/project'
import { CreateProjectParams, AreaType } from 'models/project'
import { Confirmation } from '../shared/Confirmation'
import { AreaTools, AreaSelection } from 'models/editor'
import { IsochroneDialog } from '../shared/IsochroneDialog'
import { RegionDialog } from '../shared/RegionDialog'
import { RegionFormParams } from 'models/region'
import { Dialog } from 'components/Dialog'
import { Button } from 'components/Button'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useTranslation } from 'react-i18next'
import { getCircleData } from 'utils/areaDetails'
import { useMapEditorContext } from 'context/map-editor-context'
import { FileUploadDialog } from '../shared/FileUploadDialog'

export const ChangeProjectArea = () => {
  const { t } = useTranslation()
  const [{ editor, areaTool, confirmed }, mapEditorDispatch] = useMapEditorContext()
  const { projectId } = useParams()
  const [changeArea, { status }] = useChangeArea(projectId)
  const [showWarning, setShowWarning] = useState(true)
  const selectedFeature = editor.feature!

  const submitArea = (params: CreateProjectParams) => {
    return changeArea(params, {
      onSuccess: () => {
        mapEditorDispatch.featureCreated()
      },
    })
  }

  // isochrones dialog
  const createPointArea = (data: CreateProjectParams) => {
    submitArea({
      ...data,
      coordinates: selectedFeature.geometry.coordinates as PointCoordinates,
    })
  }

  const createPolygon = (areaType: AreaType) => {
    mapEditorDispatch.setConfirm(true)
    submitArea({ areaType, geometry: selectedFeature.geometry as Polygon })
  }

  // With radius tool
  const createRadius = (areaType: AreaType) => {
    const { circleCenter, radius } = getCircleData(selectedFeature as any)
    mapEditorDispatch.setConfirm(true)
    submitArea({
      areaType,
      coordinates: circleCenter as PointCoordinates,
      radius,
    })
  }

  const createRegion = (data: RegionFormParams) => {
    submitArea({ areaType: AreaType.REGION, region: data })
  }

  const onCancel = () => {
    setShowWarning(false)
    mapEditorDispatch.cancelWizard()
  }

  if (showWarning) {
    return (
      <Dialog aria-label={t('change_project_area.title')} onClose={onCancel} isOpen={true}>
        <Stack space="xlarge">
          <Stack space="small">
            <Heading>{t('change_project_area.title')}</Heading>
            <Text>{t('change_project_area.subtitle')}</Text>
          </Stack>
          <Stack space="medium">
            <Button onClick={onCancel} variant="secondary" full>
              {t('change_project_area.cancel')}
            </Button>
            <Button onClick={() => setShowWarning(false)} variant="danger" full>
              {t('change_project_area.continue')}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    )
  }

  if (!confirmed) {
    const onConfirmFor = (areaTool: AreaSelection) => {
      switch (areaTool) {
        case AreaTools.RADIUS:
          return () => createRadius(AreaType.RADIUS)
        case AreaTools.POLYGON:
          return () => createPolygon(AreaType.POLYGON)
        default:
          return () => mapEditorDispatch.setConfirm(true)
      }
    }

    return <Confirmation onConfirm={onConfirmFor(areaTool)} />
  }

  if (areaTool === AreaTools.POINT) {
    return <IsochroneDialog onClose={onCancel} onConfirm={createPointArea} status={status} />
  }

  if (areaTool === AreaTools.REGION) {
    return (
      <RegionDialog
        onClose={() => mapEditorDispatch.featureCreated()}
        onConfirm={createRegion}
        status={status}
      />
    )
  }

  if (areaTool === AreaTools.FILE_UPLOAD) {
    return (
      <FileUploadDialog
        onClose={() => mapEditorDispatch.featureCreated()}
        onConfirm={submitArea}
        status={status}
      />
    )
  }

  return null
}

ChangeProjectArea.displayName = 'ChangeProjectArea'
